import { useMemo } from 'react'

import { useRoute } from '@react-navigation/native'
import { Icons, Illustrations } from 'atoms'
import { AuthData, HIRING_SCREENS_ENUM } from 'integration/resources/auth'
import { FORMATION_TOKEN_KEY } from 'integration/resources/auth/requests'
import { MENU_FEATURES_ENUM, useGetHighlightedFeaturesQuery } from 'integration/resources/menu'
import { Box, ScrollView, Text, VStack } from 'native-base'
import { StyleSheet, Platform } from 'react-native'
import { useQueryClient } from 'react-query'
import { ZendeskAPI } from 'react-zendesk'
import screensNames from 'src/constants/screensNames'
import { useAccessLogs } from 'src/hooks/useAccessLogs'
import { useAuthAtomValue, useSetAuthAtom } from 'src/store/auth'
import { useSetTokenAtom } from 'src/store/token'
import { Storage } from 'src/utils'
import { getDeviceId } from 'src/utils/device'
import { LogEvent } from 'src/utils/logEvents'

import { ItemMenu } from './ItemMenu'
import Appconfig from '../../../../../app.config'

const allowedOrigins = ['https://hml.portalmaisturbo.com.br', 'https://portalmaisturbo.com.br']

const isOriginAllowed = (referrer: string) =>
  allowedOrigins.some((origin) => referrer.startsWith(origin))

export const handleSendSSOAuthDataFormation = async (authUser: AuthData | null) => {
  const originLocation = window?.location?.origin

  if (!isOriginAllowed(originLocation)) {
    // eslint-disable-next-line no-console
    console.error('Origin not allowed:', originLocation)

    return
  }

  const deviceId = await getDeviceId()

  const tokenFormation = await Storage.getItem(FORMATION_TOKEN_KEY) // this token is set in the pre-auth

  const targetOrigin = process.env.FORMACAO_PORTAL_URL || 'https://portal.formacaoturbo.com.br'

  const message = {
    type: 'SSO_AUTH_DATA_PORTAL',
    ssoInfo: authUser,
    deviceId
  }

  const newWindow = window.open(
    `${targetOrigin}/autenticacao-sso?token=${tokenFormation}`,
    '_blank'
  )

  LogEvent(`click_botao_formacao`, {
    std_code: authUser?.user?.std_code,
    user_name: authUser?.user?.name,
    user_email: authUser?.user?.email
  })

  setTimeout(() => {
    newWindow?.postMessage(message, targetOrigin)
  }, 1500)
}

export const handleSendSSOAuthDataAccelerate = async (authUser: AuthData | null) => {
  const originLocation = window?.location?.origin

  if (!isOriginAllowed(originLocation)) {
    // eslint-disable-next-line no-console
    console.error('Origin not allowed:', originLocation)

    return
  }

  const deviceId = await getDeviceId()

  const tokenSSO = await Storage.getItem(FORMATION_TOKEN_KEY) // this token is set in the pre-auth

  const targetOrigin = process.env.ACELERA_URL || 'https://painel.aceleraconsultor.com.br'

  const message = {
    type: 'SSO_AUTH_DATA_PORTAL',
    ssoInfo: authUser,
    deviceId
  }

  const newWindow = window.open(
    `${targetOrigin}/recrutador/autenticacao-sso?token=${tokenSSO}`,
    '_blank'
  )

  LogEvent(`click_botao_acelera`, {
    std_code: authUser?.user?.std_code,
    user_name: authUser?.user?.name,
    user_email: authUser?.user?.email
  })

  setTimeout(() => {
    newWindow?.postMessage(message, targetOrigin)
  }, 1500)
}

export const Sidebar = () => {
  const queryClient = useQueryClient()

  const route = useRoute()

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''

  const initialPageName = useMemo(() => {
    if (Platform.OS !== 'web') return ''

    const matchers = [
      { check: (path: string) => path === '/' || path.startsWith('/home'), name: 'Início' },
      { check: (path: string) => path.startsWith('/perfil'), name: 'Perfil' },
      { check: (path: string) => path.startsWith('/carteira'), name: 'Carteira' },
      { check: (path: string) => path.startsWith('/minha-organizacao'), name: 'Oportunidades' },
      { check: (path: string) => path.startsWith('/conteudos'), name: 'Conteúdos' },
      { check: (path: string) => path.startsWith('/paineis'), name: 'Painéis' },
      { check: (path: string) => path.startsWith('/pesquisas'), name: 'Pesquisas' },
      { check: (path: string) => path.startsWith('/contrata-ja'), name: 'Contrata Já' },
      { check: (path: string) => path.startsWith('/contratacao'), name: 'Contratação' },
      { check: (path: string) => path.startsWith('/hora-em-hora'), name: 'Hora em Hora' }
    ]

    const matched = matchers.find(({ check }) => check(currentPath))?.name

    if (matched) return matched

    const routeName = route?.name as keyof typeof screensNames | undefined

    if (routeName && screensNames[routeName]) {
      return screensNames[routeName]
    }

    return currentPath || 'Página desconhecida'
  }, [currentPath, route?.name])

  const { registerNavigation } = useAccessLogs({
    initialPageName,
    initialPagePath: currentPath
  })

  const { data } = useGetHighlightedFeaturesQuery()

  const featuredData = useMemo(() => data?.data?.data, [data?.data?.data])

  const dashboardsHasHighlight = featuredData?.filter(
    (item) => item.menu_key === MENU_FEATURES_ENUM.DASHBOARDS && !item.read
  )[0]

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const surveysHasHighlight = featuredData?.filter(
    (item) => item.menu_key === MENU_FEATURES_ENUM.SURVEYS && !item.read
  )[0]

  const setAuthAtom = useSetAuthAtom()

  const setAuthToken = useSetTokenAtom()

  const authAtom = useAuthAtomValue()

  const handleLogout = () => {
    setAuthAtom(null)

    setAuthToken(null)

    queryClient.removeQueries()
  }

  // const isDev = process.env.Environment !== 'prd'

  const authAtomValue = useAuthAtomValue()

  const accessFlags = useMemo(() => authAtomValue?.user, [authAtomValue?.user])

  const seeCockpit = accessFlags?.is_visible_cockpit

  const seeAccelerate = accessFlags?.is_visible_accelerate

  const seeWalletManagement = accessFlags?.is_visible_wallet_management

  const seeGoals = accessFlags?.is_visible_goals

  const seeDelegation = accessFlags?.is_visible_delegation

  const seeMigration = accessFlags?.is_visible_migration

  const seeHourly = accessFlags?.is_visible_hourly

  const handleToggleZendesk = () => {
    ZendeskAPI('webWidget', 'open')

    ZendeskAPI('webWidget', 'setLocale', 'pt-BR')

    ZendeskAPI('webWidget', 'helpCenter:setSuggestions', { search: 'login' })
  }

  const hiringItems = []

  if (seeWalletManagement) {
    hiringItems.push({
      id: 1,
      title: 'Carteirização',
      path: `/contratacao/${HIRING_SCREENS_ENUM.WALLETS_MANAGEMENT}`,
      testID: 'walletsManagement'
    })
  }

  if (seeGoals) {
    hiringItems.push({
      id: 2,
      title: 'Metas',
      path: `/contratacao/${HIRING_SCREENS_ENUM.GOALS}`,
      testID: 'goals'
    })
  }

  if (seeDelegation) {
    hiringItems.push({
      id: 3,
      title: 'Delegação',
      path: `/contratacao/${HIRING_SCREENS_ENUM.DELEGATION}`,
      testID: 'delegation'
    })
  }

  if (seeMigration) {
    hiringItems.push({
      id: 4,
      title: 'Migração',
      path: `/contratacao/${HIRING_SCREENS_ENUM.MIGRATION}`,
      testID: 'migration'
    })
  }

  const showHiringMenu = useMemo(
    () => !!seeWalletManagement || !!seeGoals || !!seeDelegation || !!seeMigration,
    [seeWalletManagement, seeGoals, seeDelegation, seeMigration]
  )

  return (
    <VStack w="255px" position="relative" p={8} pr={0} pb={0} bg="background.base">
      <Box style={styles.background} />
      <VStack space={4}>
        <Icons.LogoTurbo size={130} />
        <Box opacity={1}>
          <Icons.PortalMaisTurbo />
        </Box>
      </VStack>
      <ScrollView showsVerticalScrollIndicator={false} position="relative" zIndex={3}>
        <Box height="83vh" justifyContent="space-between" mt={10} position="relative">
          <Box>
            <VStack space={4}>
              <ItemMenu
                testID="start"
                icon="FlameSantander"
                title="Início"
                path="/home"
                onNavigate={registerNavigation}
              />
              <ItemMenu
                testID="profile"
                icon="User"
                title="Perfil"
                path="/perfil"
                onNavigate={registerNavigation}
              />
              <ItemMenu
                testID="wallet"
                icon="Suitcase"
                title="Carteira"
                onNavigate={registerNavigation}
                submenus={[
                  {
                    id: 1,
                    title: 'Contatos',
                    path: '/carteira/contatos',
                    testID: 'contacts'
                  },
                  {
                    id: 2,
                    title: 'TABs',
                    path: '/carteira/tabs',
                    testID: 'tabs'
                  }
                ]}
              />
              <ItemMenu
                isHighlighted={!!dashboardsHasHighlight}
                testID="opportunities"
                icon="Calendar"
                title="Oportunidades"
                path="/minha-organizacao/oportunidades/5"
                onNavigate={registerNavigation}
              />
              {showHiringMenu && (
                <ItemMenu
                  testID="hiring"
                  icon="WalletsManagement"
                  title="Contratação"
                  onNavigate={registerNavigation}
                  submenus={hiringItems}
                />
              )}
              <ItemMenu
                testID="contents"
                icon="BookOpen"
                title="Conteúdos"
                path="/conteudos"
                onNavigate={registerNavigation}
              />
              <ItemMenu
                isHighlighted={!!dashboardsHasHighlight}
                testID="dashboards"
                icon="Dashboards"
                title="Painéis"
                path="/paineis"
                onNavigate={registerNavigation}
              />
              <ItemMenu
                // isHighlighted={!!surveysHasHighlight}
                testID="survey"
                icon="Survey"
                title="Pesquisas"
                path="/pesquisas"
                onNavigate={registerNavigation}
              />
              <ItemMenu
                testID="hire-more"
                icon="Plus"
                title="Contrata Já"
                path="/contrata-ja"
                onNavigate={registerNavigation}
              />

              <ItemMenu
                testID="ecosystem"
                icon="Cockpit"
                title="Ecossistema"
                onNavigate={registerNavigation}
                submenus={[
                  ...(seeCockpit
                    ? [
                        {
                          id: 1,
                          title: 'ChamaAI',
                          path: '',
                          onPress: () => {
                            LogEvent(`menu_link_cockpit`, {
                              item_id: ''
                            })

                            window.open('https://cockpitai.com.br/', '_blank', 'noopener')
                          },
                          testID: 'cockpit'
                        }
                      ]
                    : []),
                  {
                    id: 2,
                    title: 'Formação',
                    path: '',
                    onPress: () => handleSendSSOAuthDataFormation(authAtom ?? null),
                    testID: 'formation'
                  },
                  ...(seeAccelerate
                    ? [
                        {
                          id: 3,
                          title: 'Acelera',
                          path: '',
                          onPress: () => handleSendSSOAuthDataAccelerate(authAtom ?? null),
                          testID: 'accelerate'
                        }
                      ]
                    : [])
                ]}
              />
              {seeHourly && (
                <ItemMenu
                  testID="hourly"
                  icon="Clock"
                  title="Hora em Hora"
                  path="/hora-em-hora"
                  onNavigate={registerNavigation}
                />
              )}
            </VStack>
            <Box h="1px" bg="gray.600" opacity={0.6} my={10} w="full" maxW="193px" mr={6} />
            <VStack space={4}>
              <ItemMenu
                testID="global-support"
                icon="Support"
                title="Suporte"
                onPress={handleToggleZendesk}
              />
              <ItemMenu
                testID="global-logout"
                icon="GoOut"
                title="Sair do Turbo"
                onPress={handleLogout}
              />
            </VStack>
          </Box>
          <Box position="relative" left={4} mt={10}>
            <Text
              fontSize="14px"
              lineHeight="20px"
              color="white"
              fontWeight={600}>{`versão ${Appconfig.version}`}</Text>
          </Box>
        </Box>
      </ScrollView>

      <Box
        position="absolute"
        right={0}
        bottom={0}
        zIndex={1}
        borderBottomRightRadius="20px"
        overflow="hidden">
        <Illustrations.FlameSantanderTurbo />
      </Box>
    </VStack>
  )
}

const styles = StyleSheet.create({
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    backgroundImage: 'linear-gradient(146.76deg, #494848 4.39%, #393939 42.03%, #070707 94.18%)'
  }
})
